import { useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, Film, Search, SearchX, Sparkles, X } from 'lucide-react';

const normalize = value => String(value || '').normalize('NFKC').toLocaleLowerCase().trim();

export default function CanvasSourcePicker({ kind, items, value, onChange, disabled, loading, error, onRetry }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();
  const isScript = kind === 'script';
  const noun = isScript ? '剧本' : '案例';
  const Icon = isScript ? Film : Sparkles;
  const selected = items.find(item => String(item.id) === String(value));
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  const indexedItems = useMemo(() => items.map(item => ({ item, text: normalize([
    item.title, item.sub_title, item.genre, item.category_name, item.tag,
  ].filter(Boolean).join(' ')) })), [items]);
  const results = indexedItems.filter(({ text }) => terms.every(term => text.includes(term))).map(({ item }) => item);

  const clearSearch = () => { setQuery(''); inputRef.current?.focus(); };
  const handleResultKeys = event => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const buttons = Array.from(listRef.current.querySelectorAll('button:not(:disabled)'));
    if (!buttons.length) return;
    event.preventDefault();
    const index = buttons.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
      : Math.max(0, Math.min(buttons.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
    buttons[next]?.focus();
  };

  return <section className="canvas-source-picker" aria-labelledby={`${id}-label`}>
    <div className="canvas-source-heading">
      <label id={`${id}-label`} htmlFor={`${id}-search`}>{isScript ? '剧本项目' : '选择案例'}</label>
      <span id={`${id}-count`} role="status" aria-live="polite" aria-atomic="true">
        {!loading && !error && (terms.length ? `${results.length} 个匹配 / 共 ${items.length} 个${noun}` : `共 ${items.length} 个${noun}`)}
      </span>
    </div>
    <div className="canvas-source-search">
      <Search size={17} aria-hidden="true" />
      <input id={`${id}-search`} ref={inputRef} type="search" aria-label={`搜索${noun}`} autoComplete="off"
        aria-controls={`${id}-results`} aria-describedby={`${id}-count`} value={query} disabled={disabled}
        placeholder={isScript ? '搜索剧本名称、题材…' : '搜索案例名称、简介、分类…'}
        onChange={event => { setQuery(event.target.value); if (listRef.current) listRef.current.scrollTop = 0; }}
        onKeyDown={event => {
          if (event.key === 'Enter') event.preventDefault();
          if (event.key === 'ArrowDown') { event.preventDefault(); listRef.current?.querySelector('button:not(:disabled)')?.focus(); }
          if (event.key === 'Escape' && query) { event.preventDefault(); event.stopPropagation(); clearSearch(); }
        }} />
      {query && <button type="button" className="canvas-source-clear" aria-label={`清空${noun}搜索`} disabled={disabled} onClick={clearSearch}><X size={14} /></button>}
      <span className="canvas-source-search-hint" aria-hidden="true">搜索</span>
    </div>
    <div id={`${id}-results`} className="canvas-source-results" ref={listRef} aria-busy={loading}>
      {loading ? <div className="canvas-source-empty" role="status"><span className="canvas-source-loading" aria-hidden="true"><Icon size={22} /></span><strong>正在加载{noun}…</strong></div>
        : error ? <div className="canvas-source-empty" role="alert"><strong>{error}</strong><button type="button" onClick={onRetry}>重新加载</button></div>
          : !results.length ? <div className="canvas-source-empty">
            <SearchX size={24} strokeWidth={1.5} aria-hidden="true" />
            <strong>{items.length ? `没有找到匹配的${noun}` : isScript ? '还没有可用剧本' : '暂无已发布案例'}</strong>
            <p>{items.length ? '换个关键词试试，也可以清空搜索查看全部。' : isScript ? '先到写作台完成并采用一集正文。' : '可以先从空白画布或自己的媒体开始。'}</p>
            {query && <button type="button" disabled={disabled} onClick={clearSearch}>清空搜索</button>}
          </div>
            : <ul aria-label={`${noun}搜索结果`} onKeyDown={handleResultKeys}>
              {results.map(item => {
                const chosen = String(item.id) === String(value);
                const cover = !isScript && (item.cover || item.image_url);
                const detail = isScript ? item.genre || '短剧剧本' : item.sub_title || item.category_name || item.tag || '已发布案例';
                return <li key={item.id}>
                  <button type="button" className={`canvas-source-result${chosen ? ' is-selected' : ''}`} aria-pressed={chosen}
                    aria-label={`选择${noun}：${item.title || '未命名'}`} disabled={disabled} onClick={() => onChange(String(item.id))}>
                    <span className="canvas-source-thumbnail"><Icon size={18} aria-hidden="true" />{cover && <img src={cover} alt="" loading="lazy" onError={event => { event.currentTarget.style.display = 'none'; }} />}</span>
                    <span className="canvas-source-result-copy"><strong>{item.title || '未命名'}</strong><small>{detail}</small></span>
                    <span className="canvas-source-result-state" aria-hidden="true">{chosen ? <><span>已选</span><Check size={15} /></> : <ChevronRight size={15} />}</span>
                  </button>
                </li>;
              })}
            </ul>}
    </div>
    {selected && <div className="canvas-source-selection" role="status"><Check size={13} aria-hidden="true" /><span>已选{noun}：<strong>{selected.title || '未命名'}</strong></span></div>}
  </section>;
}
